import type { Attachment, MessageRead } from '../client'
import type { StreamEvent } from './chatStream'
import { messageBlocks } from './messageContent'

/** A streaming turn is a chronological list of blocks: reasoning boxes interleaved with
 * the assistant's own bubbles, plus previews of images a tool produced. Pure throughout —
 * `reduce` folds one event into the list, `drain` reveals buffered characters. */

type Streamed = { text: string; pending: string }

export type ReasoningEvent =
  | ({ type: 'thinking'; parent: string | null } & Streamed)
  | ({ type: 'text'; parent: string } & Streamed)
  | {
      type: 'tool_use'
      toolUseId: string
      toolName: string
      displayName: string | null
      status: 'pending' | 'running' | 'complete'
      parent: string | null
    }

export type Block =
  | { kind: 'reasoning'; events: ReasoningEvent[] }
  | ({ kind: 'output' } & Streamed)
  | { kind: 'preview'; attachments: Attachment[] }

const text = (value: unknown) => (typeof value === 'string' ? value : '')
const optional = (value: unknown) => (typeof value === 'string' && value ? value : null)

/** Preview blocks are transparent to the flow: a burst of tool images between two
 * thinking deltas should not split the reasoning box in two. */
function trailing<K extends Block['kind']>(blocks: Block[], kind: K) {
  for (let index = blocks.length - 1; index >= 0; index--) {
    const block = blocks[index]
    if (block.kind === kind) return block as Extract<Block, { kind: K }>
    if (block.kind !== 'preview') return undefined
  }
  return undefined
}

function addReasoning(blocks: Block[], event: ReasoningEvent): Block[] {
  const block = trailing(blocks, 'reasoning')
  if (!block) return [...blocks, { kind: 'reasoning', events: [event] }]

  const last = block.events.at(-1)
  const sameSlot =
    last && last.type === event.type && last.type !== 'tool_use' && last.parent === event.parent

  const events = sameSlot
    ? block.events.map((current, index) =>
        index === block.events.length - 1 && current.type !== 'tool_use' && event.type !== 'tool_use'
          ? { ...current, pending: current.pending + event.pending }
          : current,
      )
    : [...block.events, event]

  return blocks.map((current) => (current === block ? { ...block, events } : current))
}

function addOutput(blocks: Block[], chunk: string): Block[] {
  const block = trailing(blocks, 'output')
  if (!block) return [...blocks, { kind: 'output', text: '', pending: chunk }]
  return blocks.map((current) =>
    current === block ? { ...block, pending: block.pending + chunk } : current,
  )
}

function setToolStatus(blocks: Block[], toolUseId: string, status: 'running' | 'complete'): Block[] {
  return blocks.map((block) =>
    block.kind === 'reasoning'
      ? {
          ...block,
          events: block.events.map((event) =>
            event.type === 'tool_use' && event.toolUseId === toolUseId ? { ...event, status } : event,
          ),
        }
      : block,
  )
}

export function reduce(blocks: Block[], { event, data }: StreamEvent): Block[] {
  switch (event) {
    case 'thinking_delta':
      return addReasoning(blocks, {
        type: 'thinking',
        text: '',
        pending: text(data.text),
        parent: optional(data.parent_tool_use_id),
      })

    case 'text_delta': {
      const parent = optional(data.parent_tool_use_id)
      // A sub-agent's reply belongs inside the reasoning box; the parent's own text
      // is what the user came for, so it opens a chat bubble.
      const chunk = text(data.text)
      if (!chunk) return blocks
      return parent
        ? addReasoning(blocks, { type: 'text', text: '', pending: chunk, parent })
        : addOutput(blocks, chunk)
    }

    case 'tool_use_start':
      return addReasoning(blocks, {
        type: 'tool_use',
        toolUseId: text(data.tool_use_id),
        toolName: text(data.tool_name),
        displayName: optional(data.display_name),
        status: 'pending',
        parent: optional(data.parent_tool_use_id),
      })

    case 'tool_use_input_done':
      return setToolStatus(blocks, text(data.tool_use_id), 'running')

    case 'tool_result':
      return setToolStatus(blocks, text(data.tool_use_id), 'complete')

    case 'tool_result_preview': {
      const attachments = (Array.isArray(data.attachments) ? data.attachments : []).filter(
        (item): item is Attachment => Boolean(item) && typeof item === 'object' && 'url' in item,
      )
      if (attachments.length === 0) return blocks

      const last = blocks.at(-1)
      return last?.kind === 'preview'
        ? blocks.map((block) =>
            block === last ? { ...last, attachments: [...last.attachments, ...attachments] } : block,
          )
        : [...blocks, { kind: 'preview', attachments }]
    }

    default:
      return blocks
  }
}

/** Reveal buffered characters at a steady rate, so the reply reads smoothly however
 * bursty the upstream chunks were. `all` empties the buffer when the turn ends. */
export function drain(blocks: Block[], all = false): Block[] {
  const step = (slot: Streamed): Streamed | null => {
    if (!slot.pending) return null
    const size = all ? slot.pending.length : Math.max(1, Math.ceil(slot.pending.length / 30))
    return { text: slot.text + slot.pending.slice(0, size), pending: slot.pending.slice(size) }
  }

  let touched = false
  const next = blocks.map((block) => {
    if (block.kind === 'output') {
      const moved = step(block)
      if (!moved) return block
      touched = true
      return { ...block, ...moved }
    }

    if (block.kind !== 'reasoning') return block

    let blockTouched = false
    const events = block.events.map((event) => {
      if (event.type === 'tool_use') return event
      const moved = step(event)
      if (!moved) return event
      blockTouched = true
      return { ...event, ...moved }
    })

    if (!blockTouched) return block
    touched = true
    return { ...block, events }
  })

  // an unchanged list keeps its identity, so settled blocks never re-render
  return touched ? next : blocks
}

export function hasPending(blocks: Block[]) {
  return blocks.some((block) =>
    block.kind === 'output'
      ? block.pending.length > 0
      : block.kind === 'reasoning' &&
        block.events.some((event) => event.type !== 'tool_use' && event.pending.length > 0),
  )
}


export function appendStored(
  blocks: Block[],
  content: MessageRead['content'],
  settledTools: Set<string>,
): Block[] {
  let next = blocks

  for (const block of messageBlocks(content)) {
    if (block.type === 'thinking') {
      const thinking = typeof block.thinking === 'string' ? block.thinking.trim() : ''
      if (thinking) {
        next = addReasoning(next, { type: 'thinking', parent: null, text: '', pending: thinking })
      }
      continue
    }

    if (block.type === 'tool_use') {
      const toolUseId = String(block.id ?? '')
      next = addReasoning(next, {
        type: 'tool_use',
        toolUseId,
        toolName: String(block.name ?? 'tool'),
        displayName: typeof block.display_name === 'string' ? block.display_name : null,
        status: settledTools.has(toolUseId) ? 'complete' : 'running',
        parent: null,
      })
      continue
    }

    if (block.type === 'text') {
      const answer = typeof block.text === 'string' ? block.text.trim() : ''
      if (answer) next = addOutput(next, answer)
    }
  }

  return next
}
